import { yupResolver } from "@hookform/resolvers/yup"
import React from "react"
import { Controller, useForm } from "react-hook-form"
import { useCreateReturnMutation } from "../../../../api/backend/backend-api"
import CustomAlert from "../../../../ui/atoms/Alert"
import CustomButton from "../../../../ui/atoms/Button"
import ErrorLabel from "../../../../ui/atoms/ErrorLabel"
import CustomTextInput from "../../../../ui/atoms/CustomTextInput"
import Typography from "../../../../ui/atoms/Typography"
import PhoneInput from "../../../../ui/molecules/PhoneInput"
import { CustomCol, CustomRow } from "../../../../ui/spacings"
import CountryAutocomplete from "../../../addresses/components/CountryAutocomplete"
import { CompletedOrder } from "../../types"
import classes from "./ReturnForm.module.css"
import { shippingSchema } from "./schema"
import { useLabels } from "../../../contents/hooks"

interface Props {
  order: CompletedOrder
}

const ReturnForm = ({ order }: Props) => {
  const { getLabel } = useLabels()
  const { register, control, errors, handleSubmit } = useForm({
    resolver: yupResolver(shippingSchema),
    defaultValues: {
      address2: order.shipping.shippingAddress.address2,
      address: order.shipping.shippingAddress.address,
      city: order.shipping.shippingAddress.city,
      country: order.shipping.shippingAddress.countryCode,
      firstName: order.shipping.shippingAddress.firstName,
      lastName: order.shipping.shippingAddress.lastName,
      phone: order.shipping.shippingAddress.phone,
      postalCode: order.shipping.shippingAddress.zip,
    } as any,
  })

  const [createReturn, { data, loading, error }] = useCreateReturnMutation()

  const submitReturnRequest = async (data: any) => {
    await createReturn({
      variables: {
        input: {
          orderId: order.id,
          notes: data.notes ?? "",
          returnAddress: {
            address: data.address,
            address2: data.address2 ?? "",
            firstName: data.firstName,
            lastName: data.lastName,
            zipCode: data.postalCode,
            city: data.city,
            countryCode: data.country,
            phone: data.phone,
          },
        },
      },
    })
  }

  return (
    <>
      {!data?.createReturn.success && (
        <>
          <Typography>Inserisci i dati per prenotare un ritiro</Typography>
          <CustomRow spacing>
            <CustomCol xs={12} sm={6}>
              <CustomTextInput
                className={classes.control}
                name="firstName"
                inputRef={register}
                label={getLabel("common", "first-name")}
                fullWidth
                autoComplete="off"
                error={!!errors.firstName}
              />
              <ErrorLabel
                error={!!errors.firstName}
                message={getLabel("forms", "invalid-field")}
              />
            </CustomCol>
            <CustomCol xs={12} sm={6}>
              <CustomTextInput
                className={classes.control}
                name="lastName"
                inputRef={register}
                label={getLabel("common", "last-name")}
                fullWidth
                autoComplete="off"
                error={!!errors.lastName}
              />
              <ErrorLabel
                error={!!errors.lastName}
                message={getLabel("forms", "invalid-field")}
              />
            </CustomCol>
            <CustomCol xs={12} sm={12}>
              <PhoneInput
                className={classes.control}
                name="phone"
                inputRef={register}
                label="Telefono"
                fullWidth
                error={!!errors.phone}
              />
              <ErrorLabel
                error={!!errors.phone}
                message={getLabel("forms", "invalid-field")}
              />
            </CustomCol>
            <CustomCol xs={12} sm={12}>
              <CustomTextInput
                className={classes.control}
                name="address"
                inputRef={register}
                label="Indirizzo e numero civico"
                fullWidth
                autoComplete="off"
                error={!!errors.address}
              />
              <ErrorLabel
                error={!!errors.address}
                message={getLabel("forms", "invalid-field")}
              />
            </CustomCol>
            <CustomCol xs={12} sm={12}>
              <CustomTextInput
                className={classes.control}
                name="address2"
                inputRef={register}
                label="Interno, scala, ecc. (facoltativo)"
                fullWidth
                autoComplete="off"
                error={!!errors.address2}
              />
              <ErrorLabel
                error={!!errors.address2}
                message={getLabel("forms", "invalid-field")}
              />
            </CustomCol>
            <CustomCol xs={12} sm={6}>
              <CustomTextInput
                className={classes.control}
                name="postalCode"
                inputRef={register}
                label="Codice Postale"
                fullWidth
                autoComplete="off"
                error={!!errors.postalCode}
              />
              <ErrorLabel
                error={!!errors.postalCode}
                message={getLabel("forms", "invalid-field")}
              />
            </CustomCol>
            <CustomCol xs={12} sm={6}>
              <CustomTextInput
                className={classes.control}
                name="city"
                inputRef={register}
                label="Città"
                fullWidth
                autoComplete="off"
                error={!!errors.city}
              />
              <ErrorLabel
                error={!!errors.city}
                message={getLabel("forms", "invalid-field")}
              />
            </CustomCol>
            <CustomCol xs={12} sm={12}>
              <Controller
                control={control}
                name="country"
                as={
                  <CountryAutocomplete
                    className={classes.control}
                    label="Paese"
                    fullWidth
                    error={!!errors.country}
                  />
                }
              />
              <ErrorLabel
                error={!!errors.country}
                message={getLabel("forms", "invalid-field")}
              />
            </CustomCol>
            <CustomCol xs={12} sm={12}>
              <CustomTextInput
                className={classes.control}
                name="notes"
                inputRef={register}
                label="Preferenze giorno e ora di ritiro"
                fullWidth
                autoComplete="off"
                error={!!errors.city}
              />
              <ErrorLabel
                error={!!errors.city}
                message={getLabel("forms", "invalid-field")}
              />
            </CustomCol>
          </CustomRow>
          <CustomButton
            fullWidth
            onClick={handleSubmit(submitReturnRequest)}
            className="mt-2"
            loading={loading}
          >
            Invia richiesta
          </CustomButton>
        </>
      )}
      {data?.createReturn.success && (
        <CustomAlert severity="success" className="my-4">
          Abbiamo preso in carico la tua richiesta di reso. Riceverai una mail
          con i dettagli per il ritiro entro 48 ore
        </CustomAlert>
      )}
      {error && (
        <CustomAlert severity="error" className="my-4">
          {`Ops, si è verificato un errore. Ti invitiamo a riprovare più tardi. Se
          l'errore pesiste scrivici a help@vallesi.eu`}
        </CustomAlert>
      )}
    </>
  )
}

export default ReturnForm
